import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { deriveEcosystem, ago } from './ecosystemState';
import './ecosystem.css';

// Frame for the live ecosystem scene (Dev Console tab + widget; nventr.io
// app detail). Same discipline as the website's SceneFrame: the three.js
// stack is one lazy chunk requested when the frame nears the viewport, the
// canvas only renders while on screen, and reduced-motion / no-WebGL / small
// screens get a static status list instead. `data` is the raw feed; the
// caption under the scene is the one line of words the plan allows.
//
// `fill` drops the aspect ratio so the frame fills its parent (the command
// view's stage); `scene` + `derive` pick the geometry and its state
// function ('command' / 'fleet' pass a richer state through).
const EcosystemCanvas = lazy(() => import('./EcosystemCanvas'));
export const DENSE_PX = 720;

export function staticOnly() {
  if (typeof window === 'undefined') return true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
  if (window.matchMedia('(max-width: 640px)').matches) return true;
  try {
    const c = document.createElement('canvas');
    if (!(c.getContext('webgl2') || c.getContext('webgl'))) return true;
  } catch { return true; }
  return false;
}

const DOT = { ok: 'is-ok', warn: 'is-warn', down: 'is-down', busy: 'is-busy', idle: 'is-idle', pool: 'is-pool', off: 'is-off' };

function StaticList({ s, loading = false }) {
  const here = s.viewer?.role === 'uat';
  // Before the first feed arrives nothing is known — say so, don't paint
  // "unreachable" for the first second of every visit.
  const rows = loading || !s.core ? [['instance', 'off', 'loading…'], ['monitor', 'off', '…'], ['security', 'off', '…'], ['uat twin', 'off', '…'], ['control plane', 'off', '…']] : [
    [here ? 'production' : 'instance', s.core.level, s.core.status || '—'],
    ['monitor', s.monitor.level, s.monitor.lastCheckAt ? `${s.monitor.level} · ${ago(s.monitor.lastCheckAt)}` : 'no data'],
    ['security', s.security.level, s.security.scanning ? 'scanning' : s.security.lastScanAt ? `${s.security.counts.critical || 0}c ${s.security.counts.high || 0}h · ${ago(s.security.lastScanAt)}` : 'no scan yet'],
    [here ? 'uat twin (you are here)' : 'uat twin', s.twin.level, s.twin.exists ? (s.twin.deployRunning ? `deploying ${s.twin.deployRunning}` : 'live') : 'none'],
    ['control plane', s.plane.level, s.plane.reachable ? (s.plane.updates ? 'update available' : 'connected') : 'unreachable'],
    ...s.services.filter((x) => x.kind !== 'runner').map((x) => [x.name, x.level, x.status]),
  ];
  return (
    <ul className="eco-static">
      {rows.map(([k, lvl, v]) => (
        <li key={k}><span className={`eco-dot ${DOT[lvl] || 'is-off'}`} /><span className="eco-static-k">{k}</span><span className="eco-static-v">{v}</span></li>
      ))}
    </ul>
  );
}

export function EcosystemTicker({ events, limit = 8 }) {
  if (!events?.length) return null;
  return (
    <ol className="eco-ticker">
      {events.slice(0, limit).map((e, i) => (
        <li key={`${e.at}-${i}`}>
          <span className={`eco-dot ${DOT[e.level] || 'is-off'}`} />
          <span className="eco-ticker-text">{e.text}</span>
          <span className="eco-ticker-at">{ago(e.at)}</span>
        </li>
      ))}
    </ol>
  );
}

// A scene crash must never take the page down: fall back to the static list.
class SceneBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { if (this.props.onFail) this.props.onFail(err); }
  render() { return this.state.failed ? null : this.props.children; }
}

export default function EcosystemFrame({
  data, compact = false, ticker = !compact, aspect = compact ? '4 / 3' : '16 / 7', className = '', onClick,
  fill = false, scene = 'eco', derive = deriveEcosystem, caption = true, poster = null, onPick, onHover = null, onSelect = null, selected = null, hovered = null, filter = null, telemetry = null,
}) {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [isStatic, setIsStatic] = useState(true);
  const [failed, setFailed] = useState(false);
  const state = useMemo(() => derive(data), [data, derive]);
  // Scene labels are DOM text and do not shrink with the canvas, so a
  // narrow stage drowns in words. Scale them with the frame width and drop
  // the auxiliary labels (instrument captions, ring names) below `DENSE_PX`.
  const [stage, setStage] = useState({ scale: 1, dense: false });
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      const scale = Math.max(0.82, Math.min(1, w / 820));
      setStage((cur) => (Math.abs(cur.scale - scale) < 0.02 && cur.dense === (w < DENSE_PX) ? cur : { scale, dense: w < DENSE_PX }));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { setIsStatic(staticOnly()); }, []);
  useEffect(() => {
    if (isStatic || typeof IntersectionObserver === 'undefined' || !ref.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setMounted(true); setVisible(e.isIntersecting); });
    }, { rootMargin: '160px 0px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [isStatic]);

  const Tag = onClick ? 'button' : 'div';
  return (
    <div className={`eco-frame ${compact ? 'is-compact' : ''} ${fill ? 'is-fill' : ''} ${stage.dense ? 'is-dense' : ''} ${scene === 'command' && !compact ? 'is-orbit' : ''} ${className}`} data-level={state.overall} style={{ '--stage-scale': stage.scale }}>
      <Tag
        ref={ref}
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        className={`cs-scene eco-scene ${ready ? 'is-ready' : ''}`}
        style={fill ? undefined : { aspectRatio: aspect }}
        aria-label={onClick ? 'Open the Ecosystem view' : undefined}
      >
        <div className="cs-scene-poster eco-poster">
          {isStatic || failed || !data ? (poster || <StaticList s={state} loading={!data} />) : null}
        </div>
        {onClick && <span className="eco-open-hint" aria-hidden="true">Open ecosystem ↗</span>}
        {scene === 'command' && !compact && !isStatic && !failed && ready && (
          <button type="button" className="eco-reset" onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('nv-stage-reset', { detail: 'nv.stage.cam.command' })); }} title="Drag to orbit · wheel to zoom · reset the view">reset view</button>
        )}
        {!isStatic && !failed && mounted && data && (
          <SceneBoundary onFail={(err) => { console.warn('[ecosystem] scene failed, showing static view', err); setFailed(true); }}>
            <Suspense fallback={null}>
              <EcosystemCanvas state={state} scene={scene} onPick={onPick} onHover={onHover} onSelect={onSelect} selected={selected} hovered={hovered} filter={filter} telemetry={telemetry} active={visible && document.visibilityState !== 'hidden'} compact={compact} onReady={() => setReady(true)} />
            </Suspense>
          </SceneBoundary>
        )}
      </Tag>
      {caption && <p className={`eco-caption is-${state.overall}`}>{data ? state.caption : 'loading ecosystem…'}</p>}
      {ticker && <EcosystemTicker events={state.events} />}
    </div>
  );
}
